import { createApiRef } from '@backstage/frontend-plugin-api';

import type {
  CreateRunRequest,
  ListRunsRequest,
  Run,
  RunSummary,
  Transcript,
} from '@internal/plugin-cloud-agents-common';

export type {
  ArchiveFailureCode,
  CreateRunRequest,
  DeletionFailureCode,
  EmptyReportedUser,
  ListRunsRequest,
  ReportedUser,
  Run,
  RunStatus,
  RunSummary,
  StopCause,
  Transcript,
  TranscriptBlock,
  TranscriptEntry,
  TranscriptSource,
  User,
  WorkflowType,
} from '@internal/plugin-cloud-agents-common';

/**
 * Expected failures of an operator action, as values. `request_failed` covers
 * transport and server errors, where the service may still have applied the
 * action. `message` is bounded display text, never an upstream body.
 */
export type CloudAgentsActionFailure = {
  ok: false;
  kind: 'conflict' | 'not_found' | 'request_failed' | 'invalid_download';
  /** The service's machine-readable code when it sent a well-formed one. */
  code?: string;
  message: string;
};

export type CloudAgentsActionResult<T> =
  | { ok: true; value: T }
  | CloudAgentsActionFailure;

export interface CloudAgentsApi {
  /** The client reports the signed-in user as the initiator; callers cannot choose one. */
  createRun(request: Omit<CreateRunRequest, 'user'>): Promise<{ id: string }>;
  listRuns(request: ListRunsRequest): Promise<{ items: RunSummary[] }>;
  getRun(runId: string): Promise<Run>;
  getTranscript(runId: string): Promise<Transcript>;
  deleteSandbox(runId: string): Promise<void>;
  /** Completes the archival step of an exhausted run without exports. */
  waiveArchive(runId: string): Promise<CloudAgentsActionResult<void>>;
  /** Records that the run's resources were already removed outside Cloud Agents. */
  acknowledgeManualCleanup(
    runId: string,
  ): Promise<CloudAgentsActionResult<void>>;
  /**
   * The complete stored session archive. The Blob is only returned once its
   * size equals the declared `Content-Length`; anything less is a failure value.
   */
  downloadSessionArchive(runId: string): Promise<CloudAgentsActionResult<Blob>>;
}

export const cloudAgentsApiRef = createApiRef<CloudAgentsApi>().with({
  id: 'plugin.cloud-agents.service',
});
